/* eslint-disable import/prefer-default-export */
import { computed } from 'vue';
import { defineStore } from 'pinia';

import useSessionStorage from '@/composables/storage/useSessionStorage';
import { useGlobalStore } from '@/stores/globalStore';

import useFormValidation from '@/composables/validations/useFormValidation';
import filters from '@/composables/store/filters';
import usePaymentFilters from '@/composables/filters/usePaymentFilters';
import useBlockchainFilters from '@/composables/filters/useBlockchainFilters';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';

const {
  checkSessionStorage,
  bindSessionStorage,
  checkPaymentSessionStorage,
} = useSessionStorage();
const {
  validateFiatField,
  validateCoinField,
  validateWalletAddressField,
  validateWalletAddressTag,
} = useFormValidation();
const { filterPaymentLimit, filterPaymentProvider } = usePaymentFilters();
const { getActiveCoinData } = useBlockchainFilters();
const {
  formHasErrors,
  getErrorByField,
  getFormPayload,
  getErrorInForm,
  isOrderFromTheApi,
} = filters();
const { getFiats, getCoins } = useOrderEndpoints();

export const formObject = {
  fiatAmount: {
    value: '100',
    error: '',
    required: true,
    fromApi: false,
    validator: validateFiatField,
  },
  fiatType: {
    value: 'USD',
    required: true,
    fromApi: false,
  },
  coinAmount: {
    value: '0.001',
    error: '',
    required: true,
    fromApi: false,
    validator: validateCoinField,
  },
  coinType: {
    value: 'BTC',
    required: true,
    fromApi: false,
  },
  lockFiat: {
    value: true,
    required: true,
  },
  walletAddress: {
    value: null,
    error: null,
    required: true,
    fromApi: false,
    validator: computed(() => validateWalletAddressField),
  },
  blockchain: {
    value: {
      value: 'BTC',
      name: 'Bitcoin',
    },
    required: true,
    fromApi: false,
  },
  walletAddressTag: {
    value: '',
    required: false,
    fromApi: false,
    validator: validateWalletAddressTag,
  },
  payment: {
    value: null,
    fromApi: false,
    required: true,
  },
  subscriptionId: {
    value: null,
    required: false,
  },
};
export const useBuyOrderFormStore = defineStore({
  id: 'buyOrder',
  state: () => ({
    form: {
      ...checkSessionStorage('buyOrder', formObject),
      ...{
        payment: checkPaymentSessionStorage('buyOrder', formObject),
      },
    },
    coinData: {
      data: checkSessionStorage('coinData', []),
      coinType: checkSessionStorage('coinType', formObject.coinType.value),
    },
    fiatData: {
      data: checkSessionStorage('fiatData', []),
      fiatType: checkSessionStorage('fiatType', formObject.fiatType.value),
    },
    feeData: {
      networkFee: 0,
      gatewayFee: 0,
      receivedRate: 0,
      quoteValue: 0,
      spread: 0,
      isLoading: true,
    },
    otpData: {
      email: null,
      mobile: null,
    },
  }),
  actions: {
    setCoinType(coin) {
      this.coinData.coinType = coin;
      bindSessionStorage('coinType', this.coinData.coinType);
    },
    setRequiredFieldByKey(key, isRequired) {
      this.form[key].required = isRequired;
      bindSessionStorage(this.$id, this.form);
    },
    setPaymentServiceProvider(value) {
      this.form.payment.value = value;
      bindSessionStorage(this.$id, this.form);
    },
    setPspTypeCode(value) {
      // Working of this function can be changed to include other parameters of the Payment
      this.form.payment.pspTypeCode = value;
      bindSessionStorage(this.$id, this.form);
    },
    setPspRequireMobile(value) {
      this.form.payment.require_mobile = value;
    },
    setUserEmail(value) {
      if (!value) { throw new Error('Email is required'); }
      this.otpData.email = value;
    },
    async updateValue(field, val, skipValidations = false) {
      if (!Object.getOwnPropertyDescriptor(this.form, field)) {
        return;
      }

      if (!skipValidations && Object.getOwnPropertyDescriptor(this.form[field], 'validator')) {
        const globalStore = useGlobalStore();
        const { validator } = this.form[field];
        let validatorBinding;

        switch (field) {
          case 'walletAddress':
            if (
              this.findAllBlockchainCodesByCoins
              && this.findAllBlockchainCodesByCoins.length > 0
            ) {
              validatorBinding = await validator(val, [
                this.form.blockchain.value,
              ]);
            }
            break;
          case 'fiatAmount':
            validatorBinding = validator(
              val,
              this.form.fiatType.value,
              filterPaymentLimit(
                this.form.fiatType.value,
                this.form.payment.value,
                globalStore.getPayments,
              ),
            );

            break;
          case 'coinAmount':
            validatorBinding = validator(
              val,
              this.form.coinType.value,
              this.form.blockchain.value,
              this.coinData.data,
            );
            break;
          case 'walletAddressTag':
            validatorBinding = await validator(
              val,
              this.getWalletMemoByBlockchain,
            );
            break;

          default:
            validatorBinding = validator(field, val);
        }

        this.form[field].error = validatorBinding;
      }

      this.form[field].value = val;
      bindSessionStorage(this.$id, this.form);
    },

    updateFeeData(key, value) {
      this.feeData[key] = value;
    },
    async fetchBuyFiats() {
      if (this.fiatData.data.length > 0) {
        return this.fiatData.data;
      }

      const fiats = await getFiats('buy');
      return this.setFiatData(fiats.data.data.fiats);
    },
    async fetchBuyCoins() {
      if (this.coinData.data.length > 0) {
        return this.coinData.data;
      }

      const coins = await getCoins('buy');
      return this.setCoinData(coins.data.data.coins);
    },
    setCoinData(coinResponse) {
      this.coinData.data = coinResponse;
      bindSessionStorage('coinData', this.coinData.data);
      return this.coinData.data;
    },
    setFiatData(fiatResponse) {
      this.fiatData.data = fiatResponse;
      bindSessionStorage('fiatData', this.fiatData.data);
      return this.fiatData.data;
    },
    async triggerWalletValidation(walletAddressParam, blockchain) {
      const { validator } = this.form.walletAddress;

      if (
        this.findAllBlockchainCodesByCoins
        && this.findAllBlockchainCodesByCoins.length > 0
      ) {
        this.form.walletAddress.error = await validator(walletAddressParam, [
          blockchain,
        ]);
      }
    },
  },
  getters: {
    formHasErrors: (store) => formHasErrors(store),
    getErrorInForm: (store) => getErrorInForm(store),
    getErrorByField: (store) => (field) => getErrorByField(store)(field),
    getFormPayload: (store) => store.form && getFormPayload(store),
    getWidgetFormPayload(store) {
      let accountRef = localStorage.getItem('account_ref');

      if (!accountRef) {
        accountRef = crypto.randomUUID();
        localStorage.setItem('account_ref', accountRef);
      }

      const obj = {
        source_amount: store.form.fiatAmount.value,
        source: store.form.fiatType.value,
        target_amount: store.form.coinAmount.value,
        target: store.form.coinType.value,
        wallet_address: store.form.walletAddress.value,
        blockchain: store.form.blockchain.value.code,
        wallet_address_tag: store.form.walletAddressTag.value ?? null,
        payment: store.form.payment.value,
        return_url_on_success: window.location.href,
        account_reference: accountRef,
      };

      return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== null && value !== ''),
      );
    },
    findAllBlockchainCodesByCoins() {
      // const coinData = store.getActiveCoinData;
      const activeCoin = this.coinData.coinType;
      const coinData = getActiveCoinData(this.coinData.data, activeCoin);

      if (!coinData) return [];
      return coinData.blockchains.map((el) => ({
          value: el.code,
          name: el.description,
        }));
    },

    getWalletMemoByBlockchain(store) {
      if (!store.coinData) {
        return false;
      }

      const activeCoin = store.form.coinType.value;
      const activeBlockchain = store.form.blockchain.value;
      const activeCoinData = getActiveCoinData(store.coinData.data, activeCoin);

      if (!activeCoinData) {
        return false;
      }

      const activeBlockchainData = activeCoinData.blockchains.find(
        (el) => el.code === activeBlockchain.value || el.code === activeBlockchain,
      );

      if (!activeBlockchainData || !activeBlockchainData.address_tag.name) {
        return false;
      }

      return activeBlockchainData.address_tag;
    },
    getFiatType(store) {
      if (!store.form?.fiatType) {
        return null;
      }
      return store.form.fiatType.value;
    },
    getCoinType(store) {
      if (!store.form?.coinType) {
        return null;
      }
      return store.form.coinType.value;
    },
    getRestrictedCountriesByActiveCoin() {
      const { coinType, data } = this.coinData;
      const coinData = getActiveCoinData(data, coinType);
      if (!coinData) return [];
      return coinData?.restrictions?.country;
    },
    getPaymentObject(store) {
      if (!store.form?.payment) {
        return null;
      }
      return store.form.payment;
    },
    getPaymentProvider(store) {
      const globalStore = useGlobalStore();
      return filterPaymentProvider(
        globalStore.global.payments,
        store.form.payment.value,
      );
    },
    getCoinData(store) {
      return store.coinData.data;
    },
    getTrackingInformation(store) {
      return {
        fiatAmount: store.form.fiatAmount.value,
        fiatType: store.form.fiatType.value,
        coinAmount: store.form.coinAmount.value,
        coinType: store.form.coinType.value,
        paymentMethod: store.form.payment.value,
      };
    },
    isOrderFromTheApi() {
      return isOrderFromTheApi(this.form);
    },
  },
});
